"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { tools } from "@/data/tools";
import ToolCategories from "@/components/tools/ToolCategories";
import { splitTitle } from "@/lib/utils/title";
import { getMainToolsBreadcrumb } from "@/lib/utils/metadata";
import { useSearchParams } from "next/navigation";
import Nav from "@/components/Layout/Nav";

export default function ToolsComponents() {
  const searchParams = useSearchParams();
  const urlQuery = searchParams.get("search") || "";

  const [searchQuery, setSearchQuery] = useState(urlQuery);
  const effectiveSearchQuery = urlQuery || searchQuery;

  // Filter tools based on search query
  const filteredTools = useMemo(
    () =>
      tools.filter(
        (tool) =>
          tool.name
            .toLowerCase()
            .includes(effectiveSearchQuery.toLowerCase()) ||
          tool.description
            .toLowerCase()
            .includes(effectiveSearchQuery.toLowerCase()) ||
          tool.category
            .toLowerCase()
            .includes(effectiveSearchQuery.toLowerCase()),
      ),
    [effectiveSearchQuery],
  );

  // Get unique categories from the filtered results
  const categories = useMemo(
    () => [...new Set(filteredTools.map((tool) => tool.category))],
    [filteredTools],
  );
  const breadcrumb = getMainToolsBreadcrumb();

  return (
    <main className="relative min-h-screen bg-gradient-to-br from-white via-[#faf7ff] to-white">
      <script
        id="main-tools-breadcrumb"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }}
      />
      <Nav useDarkText />

      <section className="w-full max-w-7xl mx-auto px-6 pt-48 pb-20 space-y-20">
        {/* Header */}
        <header className="text-center space-y-6">
          <h1 className="text-4xl md:text-6xl font-extrabold bg-gradient-to-r from-[#5b32b4] to-[#fb397d] bg-clip-text text-transparent">
            Free Online Image & PDF Tools
          </h1>

          {/* ✅ FIX: Included PDFs, used "Active" verbs, and kept sentences short for high readability */}
          <p className="text-gray-600 max-w-2xl mx-auto text-lg leading-relaxed">
            We help creators and developers fix images and PDFs fast. Shrink
            file sizes, convert formats, and edit your files right in your
            browser. Use the search box below to find the tool you need and get
            started.
          </p>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto pt-4">
            <div className="relative group">
              <input
                type="text"
                aria-label="Search tools"
                placeholder="Search tools (e.g. 'converter', 'instagram')..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-6 py-4 rounded-2xl border border-[#e9e1ff] bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#5b32b4] transition-all text-[#2b1d3a]"
              />
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="h-1.5 w-24 bg-gradient-to-r from-[#5b32b4] to-[#fb397d] mx-auto rounded-full" />
        </header>

        {!effectiveSearchQuery ? (
          <>
            <ToolCategories searchQuery={effectiveSearchQuery} />
            {/* Tools by Category */}
            <div className="space-y-20">
              {categories.length > 0 ? (
                categories.map((category) => (
                  <section key={category} className="space-y-10">
                    <h2 className="text-2xl md:text-3xl font-bold text-[#2b1d3a] border-l-4 border-[#fb397d] pl-4">
                      {category}
                    </h2>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                      {filteredTools
                        .filter((tool) => tool.category === category)
                        .map((tool) => (
                          <Link
                            key={tool.slug}
                            href={tool.href}
                            // This grid renders every tool in the registry
                            // (~58 cards). With the default prefetch, each card
                            // scrolling into view fetches that route's .rsc
                            // payload — one billed edge request per card, on a
                            // page most visitors only click one link from.
                            // Disabling viewport prefetch keeps hover prefetch,
                            // so an actual click still feels instant.
                            prefetch={false}
                            className="group relative rounded-[2rem] border border-[#e9e1ff] bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(91,50,180,0.15)]"
                          >
                            <div className="space-y-4">
                              <h3 className="text-xl font-bold text-[#2b1d3a] group-hover:text-[#5b32b4] transition-colors">
                                {splitTitle(
                                  tool.title ? tool.title : tool.name,
                                  1,
                                )}
                              </h3>
                              <p className="text-sm text-gray-600 leading-relaxed">
                                {tool.description}
                              </p>
                              <span className="inline-flex items-center text-sm font-semibold text-[#c3003a] group-hover:gap-3 gap-1 transition-all">
                                Use Tool →
                              </span>
                            </div>
                            <div className="absolute inset-0 rounded-[2rem] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none bg-gradient-to-br from-[#5b32b4]/5 to-[#fb397d]/5" />
                          </Link>
                        ))}
                    </div>
                  </section>
                ))
              ) : (
                <div className="text-center py-20">
                  <p className="text-gray-500 text-xl">
                    No tools found matching &quot;{effectiveSearchQuery}&quot;
                  </p>
                </div>
              )}
            </div>{" "}
          </>
        ) : (
          <>
            {/* Tools by Category */}
            <div className="space-y-20">
              {categories.length > 0 ? (
                categories.map((category) => (
                  <section key={category} className="space-y-10">
                    <h2 className="text-2xl md:text-3xl font-bold text-[#2b1d3a] border-l-4 border-[#fb397d] pl-4">
                      {category}
                    </h2>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                      {filteredTools
                        .filter((tool) => tool.category === category)
                        .map((tool) => (
                          <Link
                            key={tool.slug}
                            href={tool.href}
                            // Search-results variant of the grid above — same
                            // prefetch reasoning applies.
                            prefetch={false}
                            className="group relative rounded-[2rem] border border-[#e9e1ff] bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(91,50,180,0.15)]"
                          >
                            <div className="space-y-4">
                              <h3 className="text-xl font-bold text-[#2b1d3a] group-hover:text-[#5b32b4] transition-colors">
                                {splitTitle(tool.name, 1)}
                              </h3>
                              <p className="text-sm text-gray-600 leading-relaxed">
                                {tool.description}
                              </p>
                              <span className="inline-flex items-center text-sm font-semibold text-[#c3003a] group-hover:gap-3 gap-1 transition-all">
                                Use Tool →
                              </span>
                            </div>
                            <div className="absolute inset-0 rounded-[2rem] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none bg-gradient-to-br from-[#5b32b4]/5 to-[#fb397d]/5" />
                          </Link>
                        ))}
                    </div>
                  </section>
                ))
              ) : (
                <div className="text-center py-20">
                  <p className="text-gray-500 text-xl">
                    No tools found matching &quot;{effectiveSearchQuery}&quot;
                  </p>
                </div>
              )}
            </div>
            <ToolCategories searchQuery={effectiveSearchQuery} />
          </>
        )}

        {/* SEO Block */}
        <section className="max-w-4xl mx-auto text-center space-y-6 pt-10">
          <div className="h-1.5 w-24 bg-gradient-to-r from-[#5b32b4] to-[#fb397d] mx-auto rounded-full" />

          <h2 className="text-3xl font-bold text-[#2b1d3a]">
            Why Use Snappy-fix for Images and PDFs?
          </h2>

          {/* ✅ FIX: Includes PDF keywords, stays over 50 words to avoid "thin content", and uses simple sentences for readability */}
          <p className="text-gray-600 leading-relaxed text-lg">
            Snappy-fix offers fast, safe, and pro-level tools for both images
            and PDF documents. Whether you are a web developer building a site,
            a designer tweaking a layout, or a business owner managing files,
            our suite saves you time. You can convert formats, compress file
            sizes, and edit PDFs right in your browser. There is no need to
            download heavy apps—just pick a tool, upload your file, and get your
            new results in seconds.
          </p>
        </section>
      </section>
    </main>
  );
}
